// ============================================
// FILE: src/components/fashion/SizeGuideSheet.jsx
// ============================================
//
// z-[320] so it sits above the z-[300] product detail screen.

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

// Rough in <-> cm conversion for the unit toggle. Charts are authored in one
// unit; the other is derived rather than stored.
const CM_PER_INCH = 2.54;

const SizeGuideSheet = ({ chart, isOpen, onClose, isDesktop = false }) => {
  const [unit, setUnit] = useState(chart?.unit || 'in');

  useEffect(() => {
    setUnit(chart?.unit || 'in');
  }, [chart?.unit, chart?.id]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen || !chart) return null;

  const baseUnit = chart.unit || 'in';
  const columns = Array.isArray(chart.columns) ? chart.columns : [];
  const rows = Array.isArray(chart.rows) ? chart.rows : [];

  const convert = (cell, columnIndex) => {
    // The first column is the size label, never a measurement.
    if (columnIndex === 0 || unit === baseUnit) return cell;

    const num = Number(cell);
    if (!Number.isFinite(num)) return cell;

    const converted = baseUnit === 'in' ? num * CM_PER_INCH : num / CM_PER_INCH;
    return converted.toFixed(1).replace(/\.0$/, '');
  };

  const panel = (
    <div
      className={
        isDesktop
          ? 'relative bg-white rounded shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto'
          : 'absolute inset-x-0 bottom-0 bg-white rounded-t shadow-2xl max-h-[80vh] overflow-y-auto'
      }
    >
      <div className="sticky top-0 bg-white px-5 pt-5 pb-3" style={{ borderBottom: '1px solid var(--fx-line)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="fx-label">Size Chart</h3>
            <p className="text-sm" style={{ color: 'var(--fx-ink-3)' }}>{chart.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close size guide"
          >
            <X size={20} className="text-[#535766]" />
          </button>
        </div>

        <div className="flex gap-2 mt-3">
          {['in', 'cm'].map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUnit(u)}
              className="px-3 py-1 text-xs font-bold transition-colors"
              style={unit === u
                ? { background: 'var(--fx-pink)', color: '#fff', borderRadius: 2 }
                : { background: '#f5f5f6', color: 'var(--fx-ink-2)', borderRadius: 2 }}
            >
              {u === 'in' ? 'Inches' : 'Centimetres'}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                {columns.map((column, i) => (
                  <th
                    key={column}
                    className={`py-2 px-3 text-left font-semibold text-[#535766] border-b border-[#eaeaec] whitespace-nowrap ${
                      i === 0 ? '' : 'text-right'
                    }`}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 ? 'bg-gray-50' : ''}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={`py-2 px-3 text-[#535766] whitespace-nowrap ${
                        cellIndex === 0 ? 'font-semibold' : 'text-right'
                      }`}
                    >
                      {convert(cell, cellIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {chart.notes && (
          <p className="text-xs text-[#94969f] mt-4 leading-relaxed">{chart.notes}</p>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`fashion-scope fixed inset-0 z-[320] ${isDesktop ? 'flex items-center justify-center p-4' : ''}`}
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      {panel}
    </div>
  );
};

export default SizeGuideSheet;
